import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authClient from '../services/authClient';
import { theme } from '../styles/design-system';
import { Wallet as WalletIcon, Plus, Minus, Clock, TrendingUp, TrendingDown } from 'lucide-react';

const Wallet = () => {
    const { user } = useAuth();
    const [balance, setBalance] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [depositAmount, setDepositAmount] = useState('');
    const [showDepositForm, setShowDepositForm] = useState(false);
    const [isDummyMoney, setIsDummyMoney] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    useEffect(() => {
        if (user) {
            loadWalletData();
        }
    }, [user]);

    const loadWalletData = async () => {
        try {
            setLoading(true);

            // Fetch balance
            const balanceRes = await authClient.makeRequest('/wallet/balance');
            if (balanceRes.ok) {
                const data = await balanceRes.json();
                setBalance(data.balance);
            }

            // Fetch transactions
            const txRes = await authClient.makeRequest('/wallet/transactions?limit=10');
            if (txRes.ok) {
                const data = await txRes.json();
                setTransactions(data.transactions);
            }
        } catch (error) {
            console.error('Error loading wallet data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeposit = async () => {
        const amount = parseFloat(depositAmount);
        if (!amount || amount <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        if (!isDummyMoney) {
            alert('Real money integration coming soon! Please use Dummy Money for now.');
            return;
        }

        try {
            const payload = {
                amount: amount,
                description: isDummyMoney ? 'Dummy Money Deposit (Paper Trading)' : 'Real Money Deposit',
                reference_id: isDummyMoney ? `DUMMY-${Date.now()}` : null
            };
            console.log('Sending deposit payload:', payload);

            const response = await authClient.makeRequest('/wallet/deposit', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                alert(data.message);
                setDepositAmount('');
                setShowDepositForm(false);
                loadWalletData(); // Reload data
            } else {
                const error = await response.json();
                alert(error.detail || 'Deposit failed');
            }
        } catch (error) {
            console.error('Error depositing:', error);
            alert('Failed to deposit funds');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2
        }).format(amount);
    };

    const styles = {
        container: {
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: theme.shadows.sm,
            maxWidth: '1000px',
            margin: '0 auto'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        title: {
            fontSize: '24px',
            fontWeight: '700',
            color: theme.colors.gray[900],
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        balanceGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '32px'
        },
        balanceCard: {
            backgroundColor: theme.colors.primary[50],
            borderRadius: '12px',
            padding: '20px',
            border: `1px solid ${theme.colors.primary[100]}`
        },
        balanceLabel: {
            fontSize: '13px',
            fontWeight: '500',
            color: theme.colors.primary[700],
            marginBottom: '8px'
        },
        balanceAmount: {
            fontSize: '28px',
            fontWeight: '700',
            color: theme.colors.primary[900]
        },
        button: {
            backgroundColor: theme.colors.primary[600],
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        section: {
            marginTop: '32px'
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.gray[900],
            marginBottom: '16px'
        },
        transactionsList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        },
        transactionItem: {
            backgroundColor: theme.colors.gray[50],
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        depositForm: {
            backgroundColor: theme.colors.gray[50],
            borderRadius: '12px',
            padding: '20px',
            marginTop: '16px'
        },
        input: {
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${theme.colors.gray[300]}`,
            fontSize: '16px',
            marginBottom: '12px'
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <p>Loading wallet...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>
                    <WalletIcon size={28} />
                    My Wallet
                </h2>
                <button style={styles.button} onClick={() => setShowDepositForm(!showDepositForm)}>
                    <Plus size={18} />
                    Add Money
                </button>
            </div>

            {/* Balance Cards */}
            {balance && (
                <div style={styles.balanceGrid}>
                    <div style={{ ...styles.balanceCard, backgroundColor: theme.colors.success[50], borderColor: theme.colors.success[100] }}>
                        <div style={{ ...styles.balanceLabel, color: theme.colors.success[700] }}>Available Balance</div>
                        <div style={{ ...styles.balanceAmount, color: theme.colors.success[900] }}>
                            {formatCurrency(balance.available)}
                        </div>
                    </div>

                    <div style={styles.balanceCard}>
                        <div style={styles.balanceLabel}>Locked in Trades</div>
                        <div style={styles.balanceAmount}>
                            {formatCurrency(balance.locked)}
                        </div>
                    </div>

                    <div style={{ ...styles.balanceCard, backgroundColor: theme.colors.secondary[50], borderColor: theme.colors.secondary[100] }}>
                        <div style={{ ...styles.balanceLabel, color: theme.colors.secondary[700] }}>Total Balance</div>
                        <div style={{ ...styles.balanceAmount, color: theme.colors.secondary[900] }}>
                            {formatCurrency(balance.total)}
                        </div>
                    </div>
                </div>
            )}

            {/* Deposit Form */}
            {showDepositForm && (
                <div style={styles.depositForm}>
                    <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Add Money to Wallet</h3>

                    {/* Money Type Toggle */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                        <button
                            onClick={() => setIsDummyMoney(true)}
                            style={{
                                flex: 1,
                                padding: '10px',
                                borderRadius: '8px',
                                border: `1px solid ${isDummyMoney ? theme.colors.success[500] : theme.colors.gray[300]}`,
                                backgroundColor: isDummyMoney ? theme.colors.success[50] : 'white',
                                color: isDummyMoney ? theme.colors.success[700] : theme.colors.gray[600],
                                fontWeight: isDummyMoney ? '600' : '400',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: `4px solid ${isDummyMoney ? theme.colors.success[500] : theme.colors.gray[300]}`
                            }} />
                            Dummy Money (Instant)
                        </button>
                        <button
                            onClick={() => setIsDummyMoney(false)}
                            style={{
                                flex: 1,
                                padding: '10px',
                                borderRadius: '8px',
                                border: `1px solid ${!isDummyMoney ? theme.colors.primary[500] : theme.colors.gray[300]}`,
                                backgroundColor: !isDummyMoney ? theme.colors.primary[50] : 'white',
                                color: !isDummyMoney ? theme.colors.primary[700] : theme.colors.gray[600],
                                fontWeight: !isDummyMoney ? '600' : '400',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                        >
                            <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                border: `4px solid ${!isDummyMoney ? theme.colors.primary[500] : theme.colors.gray[300]}`
                            }} />
                            Real Money
                        </button>
                    </div>

                    <input
                        type="number"
                        style={styles.input}
                        placeholder="Enter amount (₹)"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                    />

                    {isDummyMoney && (
                        <div style={{
                            marginBottom: '16px',
                            fontSize: '13px',
                            color: theme.colors.success[600],
                            backgroundColor: theme.colors.success[50],
                            padding: '8px 12px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <TrendingUp size={16} />
                            Funds will be added immediately for paper trading.
                        </div>
                    )}

                    {!isDummyMoney && (
                        <div style={{
                            marginBottom: '16px',
                            fontSize: '13px',
                            color: theme.colors.primary[600],
                            backgroundColor: theme.colors.primary[50],
                            padding: '8px 12px',
                            borderRadius: '6px'
                        }}>
                            Redirecting to secure payment gateway...
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button style={styles.button} onClick={handleDeposit}>
                            {isDummyMoney ? 'Add Dummy Money' : 'Proceed to Pay'} ₹{depositAmount || '0'}
                        </button>
                        <button
                            style={{ ...styles.button, backgroundColor: theme.colors.gray[400] }}
                            onClick={() => {
                                setShowDepositForm(false);
                                setDepositAmount('');
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Transaction History */}
            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Recent Transactions</h3>
                {transactions.length === 0 ? (
                    <p style={{ color: theme.colors.gray[500], textAlign: 'center', padding: '40px' }}>
                        No transactions yet
                    </p>
                ) : (
                    <div style={styles.transactionsList}>
                        {transactions.map((tx) => (
                            <div key={tx.id} style={styles.transactionItem}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: tx.type === 'deposit' ? theme.colors.success[100] :
                                            tx.type === 'withdrawal' ? theme.colors.warning[100] :
                                                theme.colors.primary[100],
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {tx.type === 'deposit' && <TrendingUp color={theme.colors.success[600]} size={20} />}
                                        {tx.type === 'withdrawal' && <TrendingDown color={theme.colors.warning[600]} size={20} />}
                                        {tx.type.includes('trade') && <Clock color={theme.colors.primary[600]} size={20} />}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '600', color: theme.colors.gray[900], textTransform: 'capitalize' }}>
                                            {tx.type.replace('_', ' ')}
                                        </div>
                                        <div style={{ fontSize: '13px', color: theme.colors.gray[500] }}>
                                            {new Date(tx.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        color: tx.type === 'deposit' || tx.type === 'profit' ? theme.colors.success[600] :
                                            tx.type === 'withdrawal' || tx.type === 'loss' ? theme.colors.error[600] :
                                                theme.colors.gray[900]
                                    }}>
                                        {tx.type === 'deposit' || tx.type === 'profit' ? '+' : '-'}
                                        {formatCurrency(tx.amount)}
                                    </div>
                                    <div style={{ fontSize: '12px', color: theme.colors.gray[500] }}>
                                        {tx.status}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Wallet;
